import React, { useState, useEffect } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GridOnIcon from "@mui/icons-material/GridOn";
import { RECORD_LIST_PATH, getDerived, sourceLabel } from "../fieldCatalog";
import {
  resolvePath,
  interpolate,
  formatValue,
  computeDerived,
  buildTableRows,
} from "../render/valueResolver";
import { loadRecordOptions, loadRecord, getSourceConfig, loadSubOrderLineItems } from "../../../services/ReportRecordLoader";
import { generateExcel, generateExcelForMany, generateExcelFromFlatRows } from "../render/excelRenderer";
import PreviewDataTable from "./PreviewDataTable";

const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

function argbToCss(argb, fb) {
  if (!argb) return fb;
  const hex = argb.length === 8 ? argb.slice(2) : argb;
  return "#" + hex;
}

export default function TemplatePreviewScreen({ open, onClose, template }) {
  const source = template.source;
  const isList = (template.sections || []).some((s) => s.type === "DATA_TABLE" && s.collectionPath === RECORD_LIST_PATH);
  const derived = getDerived(source);

  const [options, setOptions] = useState([]);
  const [records, setRecords] = useState([]); // all list rows (for list reports)
  const [picked, setPicked] = useState(null);
  const [record, setRecord] = useState(null); // selected full record / context
  const [loading, setLoading] = useState(false);
  const [start, setStart] = useState(""); // yyyy-mm-dd
  const [end, setEnd] = useState("");
  // "record" = preview one chosen sub order; "range" = preview all in a date range.
  const [mode, setMode] = useState(isList ? "range" : "record");
  const [flatRows, setFlatRows] = useState([]); // flattened line items across a range
  const [flatLoading, setFlatLoading] = useState(false);

  // A line-item (within-record collection) table previewed over a date range needs
  // the child rows flattened across all orders — a dedicated slim query, not the
  // header-only list. Scoped to SubOrder line items for now.
  const childTable = (template.sections || []).find(
    (s) => s.type === "DATA_TABLE" && s.collectionPath && s.collectionPath !== RECORD_LIST_PATH
  );
  const isFlatRange =
    mode === "range" && !!childTable && source === "SUB_ORDER" && childTable.collectionPath === "lineItems[]";

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setPicked(null);
    setRecord(null);
    setStart("");
    setEnd("");
    (async () => {
      const opts = await loadRecordOptions(source);
      if (cancelled) return;
      setOptions(opts);
      const raws = opts.map((o) => o.raw).filter(Boolean);
      setRecords(raws);
      if (isList) {
        setRecord(raws[0] || null);
        setLoading(false);
      } else if (opts.length) {
        setPicked(opts[0]);
        const full = await loadRecord(source, opts[0].id);
        if (!cancelled) setRecord(full);
        setLoading(false);
      } else {
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, source, isList]);

  // Load flattened line items when previewing a Line-Items table over a date range.
  useEffect(() => {
    if (!open || !isFlatRange) {
      setFlatRows([]);
      return undefined;
    }
    let cancelled = false;
    setFlatLoading(true);
    (async () => {
      const rows = await loadSubOrderLineItems(start || null, end || null);
      if (cancelled) return;
      setFlatRows(rows);
      setFlatLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isFlatRange, start, end, source]);

  const pick = async (opt) => {
    setPicked(opt);
    if (!opt) {
      setRecord(null);
      return;
    }
    setLoading(true);
    const full = await loadRecord(source, opt.id);
    setRecord(full);
    setLoading(false);
  };

  // Date-range filter over the loaded list (by the source's date field).
  const cfg = getSourceConfig(source);
  const dateField = cfg && cfg.dateField;
  const hasDate = !!dateField;
  const datePart = (v) => (v ? String(v).split(/[ T]/)[0] : "");
  const inRange = (obj) => {
    if (!dateField) return true;
    const d = datePart(obj && obj[dateField]);
    if (!d) return false;
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  };
  const filteredRecords = records.filter(inRange);

  // Records + context depend on the selected mode.
  const activeRecords = mode === "range" ? filteredRecords : record ? [record] : [];
  const contextRecord = mode === "range" ? filteredRecords[0] || null : record;

  const download = async () => {
    if (isFlatRange) {
      await generateExcelFromFlatRows(template, childTable, flatRows);
      return;
    }
    if (mode === "range") await generateExcelForMany(template, filteredRecords);
    else if (record) await generateExcel(template, record);
  };

  const renderSection = (sec, idx) => {
    // Cross-order flat mode: render only the title + the primary line-item table
    // (fed by the flattened rows); order-specific sections don't apply.
    if (isFlatRange) {
      if (sec.type === "DATA_TABLE") {
        if (childTable && sec.id !== childTable.id) return null;
        const cols = sec.columns || [];
        if (!cols.length) return null;
        const rowPairs = flatRows.map((r) => ({ rec: r, el: r }));
        return (
          <Box key={idx} sx={{ mb: 2 }}>
            <PreviewDataTable columns={cols} rows={rowPairs} showTotals={sec.showColumnTotals} />
          </Box>
        );
      }
      if (sec.type === "DETAIL_FIELDS" || sec.type === "FOOTER_TOTALS") return null;
    }

    if (sec.type === "TITLE") {
      const bg = argbToCss(sec.style && sec.style.fillColor, "#3B5BD9");
      const fg = argbToCss(sec.style && sec.style.fontColor, "#FFFFFF");
      return (
        <Box
          key={idx}
          sx={{
            bgcolor: bg,
            color: fg,
            textAlign: (sec.style && sec.style.align) || "center",
            fontWeight: 800,
            fontSize: (sec.style && sec.style.fontSize) || 18,
            borderRadius: 1,
            py: 1.2,
            px: 1.5,
            mb: 1.5,
          }}
        >
          {contextRecord ? interpolate(sec.text, contextRecord) : sec.text}
        </Box>
      );
    }

    if (sec.type === "TEXT") {
      return (
        <Typography key={idx} variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {contextRecord ? interpolate(sec.text, contextRecord) : sec.text}
        </Typography>
      );
    }

    if (sec.type === "SPACER") return <Box key={idx} sx={{ height: 12 }} />;

    if (sec.type === "DETAIL_FIELDS") {
      const fields = sec.fields || [];
      if (!fields.length) return null;
      return (
        <Box
          key={idx}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: sec.columnsPerRow === 1 ? "1fr" : "1fr 1fr" },
            columnGap: 3,
            rowGap: 0.5,
            mb: 1.5,
          }}
        >
          {fields.map((fl, i) => {
            const v = contextRecord ? formatValue(resolvePath(contextRecord, fl.path), fl.type) : "";
            return (
              <Box key={i} sx={{ display: "flex", gap: 1, py: 0.25, borderBottom: "1px dotted", borderColor: "divider" }}>
                <Typography variant="body2" fontWeight={700} sx={{ minWidth: 90 }}>
                  {fl.label}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: MONO }}>
                  {v === "" ? "—" : v}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }

    if (sec.type === "DATA_TABLE") {
      const columns = sec.columns || [];
      if (!columns.length) return null;
      const rowPairs = buildTableRows(contextRecord, sec, activeRecords);
      return (
        <Box key={idx} sx={{ mb: 2 }}>
          <PreviewDataTable columns={columns} rows={rowPairs} showTotals={sec.showColumnTotals} />
        </Box>
      );
    }

    if (sec.type === "FOOTER_TOTALS") {
      const rows = sec.rows || [];
      if (!rows.length) return null;
      return (
        <Box key={idx} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25, mb: 1.5 }}>
          {rows.map((r, i) => {
            const raw = r.derived ? computeDerived(contextRecord, derived[r.derived]) : resolvePath(contextRecord, r.path);
            const text = contextRecord ? formatValue(raw, r.type || "currency") : "";
            const bold = r.style && r.style.bold;
            return (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  minWidth: 240,
                  py: 0.25,
                  borderBottom: "1px dotted",
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" fontWeight={bold ? 800 : 600} color="text.secondary">
                  {r.label}
                </Typography>
                <Typography variant="body2" fontWeight={bold ? 800 : 500} sx={{ fontFamily: MONO }}>
                  {text || "—"}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }

    return null;
  };

  const showLoading = isFlatRange ? flatLoading : loading;
  const hasData = isFlatRange ? flatRows.length > 0 : mode === "range" ? filteredRecords.length > 0 : !!record;

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={onClose} title="Back to templates">
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {template.name} — Preview
          </Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<GridOnIcon />}
            onClick={download}
            disabled={loading || !hasData}
          >
            Download .xlsx
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
            <ToggleButton value="record">Single record</ToggleButton>
            <ToggleButton value="range" disabled={!hasDate}>
              Date range
            </ToggleButton>
          </ToggleButtonGroup>

          {mode === "record" ? (
            <Autocomplete
              size="small"
              sx={{ minWidth: 300 }}
              options={options}
              value={picked}
              onChange={(_, o) => pick(o)}
              getOptionLabel={(o) => (o && o.label) || ""}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(p) => <TextField {...p} label={`Sub order (${sourceLabel(source)})`} />}
            />
          ) : (
            <>
              <TextField
                type="date"
                label="From"
                size="small"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              {(start || end) && (
                <Button
                  size="small"
                  onClick={() => {
                    setStart("");
                    setEnd("");
                  }}
                >
                  Clear
                </Button>
              )}
              <Typography variant="body2" color="text.secondary">
                {isFlatRange
                  ? `${flatRows.length} line item${flatRows.length === 1 ? "" : "s"} in range`
                  : `${filteredRecords.length} ${sourceLabel(source)}${filteredRecords.length === 1 ? "" : "s"} in range`}
              </Typography>
            </>
          )}
        </Box>

        {showLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !hasData ? (
          <Typography color="text.secondary">
            {options.length === 0
              ? `No ${sourceLabel(source)} records found.`
              : mode === "record"
              ? "Select a sub order above to preview."
              : `No ${sourceLabel(source)} records in the selected range.`}
          </Typography>
        ) : (
          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
            {(template.sections || []).map((sec, idx) => renderSection(sec, idx))}
          </Paper>
        )}
      </Box>
    </Dialog>
  );
}
